import { endOfLocalDay, isIsoDate, localDateOfIso } from './isoDate.js';
import { setParams } from './urlParams.js';

/**
 * `/audit`'s URL: `?outcome=failed|refused&type=&action=&id=&actor=&from=&to=` — pasteable into
 * a ticket, the same convention as `?studio=` on the Games page (`pages/games/gamesFilter.ts`).
 * Read/write are pure and total: an unrecognised or malformed query string degrades to the empty
 * filter rather than throwing.
 */

export type AuditSegment = 'all' | 'failed' | 'refused';

export type AuditUrlFilter = {
    /** `?outcome=` — anything other than `failed` / `refused` reads as `'all'`. */
    segment: AuditSegment;
    /** `?type=` */
    resourceType: string | null;
    /** `?action=` — meaningless without a resource type, and dropped when there is none. */
    action: string | null;
    /** `?id=` — likewise. */
    resourceId: string | null;
    /** `?actor=` — `''` when absent, never `null`; it is a text input's value. */
    actor: string;
    /** `?from=YYYY-MM-DD` — dropped unless it parses as a real calendar date. */
    from: string | null;
    /** `?to=YYYY-MM-DD` */
    to: string | null;
};

export const EMPTY_AUDIT_FILTER: AuditUrlFilter = {
    segment: 'all',
    resourceType: null,
    action: null,
    resourceId: null,
    actor: '',
    from: null,
    to: null,
};

/** `null` unless `value` is `YYYY-MM-DD` AND names a real calendar day (rejects `2026-02-30`). */
const parseDate = (value: string | null): string | null => (value && isIsoDate(value) ? value : null);

export const readAuditFilter = (params: URLSearchParams): AuditUrlFilter => {
    const outcome = params.get('outcome');
    const segment: AuditSegment = outcome === 'failed' || outcome === 'refused' ? outcome : 'all';
    const resourceType = params.get('type') || null;

    return {
        segment,
        resourceType,
        // Meaningless without a resource type, so a stray `?action=` with no `?type=` reads as
        // absent rather than as a filter nothing renders a value for.
        action: resourceType ? params.get('action') || null : null,
        resourceId: resourceType ? params.get('id') || null : null,
        actor: params.get('actor') || '',
        from: parseDate(params.get('from')),
        to: parseDate(params.get('to')),
    };
};

/**
 * `null`, `''` or (for `segment`) `'all'` deletes the param rather than writing it empty, so the
 * URL never grows a `&actor=` nobody can tell apart from absent. Setting `resourceType` to a
 * falsy value also clears `action` and `resourceId` — the same "meaningless without a type" rule
 * `readAuditFilter` applies on the way in.
 */
export const writeAuditFilter = (params: URLSearchParams, patch: Partial<AuditUrlFilter>): URLSearchParams => {
    let next = setParams(params, {
        ...('segment' in patch
            ? { outcome: patch.segment === 'failed' || patch.segment === 'refused' ? patch.segment : null }
            : {}),
        ...('resourceType' in patch ? { type: patch.resourceType ?? null } : {}),
    });
    if ('resourceType' in patch && !patch.resourceType) {
        next.delete('action');
        next.delete('id');
    }
    next = setParams(next, {
        ...('action' in patch ? { action: patch.action ?? null } : {}),
        ...('resourceId' in patch ? { id: patch.resourceId ?? null } : {}),
        ...('actor' in patch ? { actor: patch.actor ?? null } : {}),
        ...('from' in patch ? { from: patch.from ?? null } : {}),
        ...('to' in patch ? { to: patch.to ?? null } : {}),
    });

    return next;
};

const AUDIT_PARAMS = ['outcome', 'type', 'action', 'id', 'actor', 'from', 'to'] as const;

/** Removes exactly the seven params above, keeping anything else the query string carries. */
export const clearAuditFilter = (params: URLSearchParams): URLSearchParams => {
    const next = new URLSearchParams(params);
    for (const key of AUDIT_PARAMS) next.delete(key);
    return next;
};

export const isFiltered = (f: AuditUrlFilter): boolean =>
    f.segment !== 'all' || f.resourceType !== null || f.actor !== '' || f.from !== null || f.to !== null;

/** What "Everything about this record" (the panel) and History's foot link both write. */
export const resourceFilterFor = (resourceType: string, resourceId: string): Partial<AuditUrlFilter> => ({
    resourceType,
    resourceId,
    action: null,
});

export type AuditListInput = {
    actor?: string;
    resourceType?: string;
    action?: string;
    resourceId?: string;
    outcome?: 'failed' | 'refused';
    from?: Date;
    to?: Date;
    limit: number;
};

/**
 * The router input the page's query sends. `from` is local midnight of `?from=`; `to` is local
 * 23:59:59.999 of `?to=` — a query for "22 September" means the whole day, not the instant it
 * starts — and neither is applied unless BOTH are set, so a lone `from` or `to` never silently
 * narrows the list to one open-ended edge nobody asked for.
 */
export const toListInput = (f: AuditUrlFilter, limit: number): AuditListInput => {
    const input: AuditListInput = { limit };

    if (f.segment === 'failed' || f.segment === 'refused') input.outcome = f.segment;

    if (f.resourceType) {
        input.resourceType = f.resourceType;
        if (f.action) input.action = f.action;
        if (f.resourceId) input.resourceId = f.resourceId;
    }

    const actor = f.actor.trim();
    if (actor) input.actor = actor;

    if (f.from && f.to) {
        input.from = localDateOfIso(f.from);
        input.to = endOfLocalDay(f.to);
    }

    return input;
};
